import type {
  DeleteRegionTaxPrepayAccountParams,
  RegionCountryTaxRateApiItem,
  RegionCountryTaxRateItem,
  RegionTaxConfigListParams,
  RegionTaxFeeApiItem,
  RegionTaxFeeItem,
  RegionTaxFeeListParams,
  RegionTaxPoolFlowApiCondition,
  RegionTaxPoolFlowApiItem,
  RegionTaxPoolFlowApiResponse,
  RegionTaxPoolFlowItem,
  RegionTaxPoolFlowListParams,
  RegionTaxPoolFlowPagingParams,
  RegionTaxPoolOverviewApiCondition,
  RegionTaxPoolOverviewApiItem,
  RegionTaxPoolOverviewApiResponse,
  RegionTaxPoolOverviewApiSummary,
  RegionTaxPoolOverviewItem,
  RegionTaxPoolOverviewListParams,
  RegionTaxPoolOverviewPagingParams,
  RegionTaxPoolOverviewSummary,
  RegionTaxPrepayAccountApiItem,
  RegionTaxPrepayAccountApiResponse,
  RegionTaxPrepayAccountItem,
  RegionTaxPrepayAccountListParams,
  RegionTaxPrepayAccountSaveResult,
  RegionTaxPrepayCustomerApiItem,
  RegionTaxPrepayCustomerApiResponse,
  RegionTaxPrepayCustomerItem,
  RegionTaxPrepayCustomerListParams,
  RegionTaxRangeConfigApiItem,
  RegionTaxRangeConfigItem,
  ReleaseRegionTaxPrepaidPoolParams,
  SaveRegionCountryTaxRateParams,
  SaveRegionTaxPrepayAccountParams,
  SaveRegionTaxPrepayCustomerParams,
  SaveRegionTaxRangeConfigParams,
} from './type/region-tax-fee'

import type { PagingRequest } from '#/api/type'

import dayjs from 'dayjs'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { DateRanges, formatDateRange } from '#/utils'

const DEFAULT_PAGE_SIZE = 1000
const DEFAULT_CONFIG_LIST_PARAMS: PagingRequest<RegionTaxConfigListParams> = {
  pageIndex: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  condition: {},
}

function formatDate(value?: dayjs.ConfigType) {
  return value ? dayjs(value).format('YYYY-MM-DD') : undefined
}

function getEffectiveEndDate(effectiveTo: null | string) {
  return effectiveTo ?? '长期'
}

function getEndMode(effectiveEndDate?: string) {
  return effectiveEndDate ? 'fixed' : 'long'
}

function parseAdAccountIds(value: string | string[]) {
  const values = Array.isArray(value) ? value : [value]

  return [
    ...new Set(
      values.flatMap((item) =>
        item
          .split(/[\s,，]+/)
          .map((part) => part.trim())
          .filter(Boolean),
      ),
    ),
  ]
}

function mapRegionTaxFeeItem(item: RegionTaxFeeApiItem): RegionTaxFeeItem {
  return {
    id: item.id,
    consumeDate: item.spendDate,
    customerId: item.merchantId,
    customerName: item.customerName,
    advertiserAccountId: item.accountId,
    advertiserAccountName: item.accountName,
    media: item.mediaPlatform,
    accountType: item.accountType,
    country: item.countryCode,
    accountCurrency: item.currency,
    taxableSpend: item.taxableSpend,
    taxRate: `${item.taxRate}%`,
    estimatedTax: item.estFee,
    poolCurrency: item.poolCurrency,
    poolDeductAmount: item.poolDeductAmount,
    deducted: item.deducted,
    exchangeRateToUsd: item.fxToUsd,
    taxUsd: item.estFeeUsd,
  }
}

function mapPrepaidOverviewSummary(
  summary: RegionTaxPoolOverviewApiSummary,
): Required<RegionTaxPoolOverviewSummary>
function mapPrepaidOverviewSummary(
  summary?: RegionTaxPoolOverviewApiSummary,
): RegionTaxPoolOverviewSummary
function mapPrepaidOverviewSummary(summary?: RegionTaxPoolOverviewApiSummary) {
  return {
    balanceTax: summary?.balanceTax,
    difference: summary?.diff,
    estimatedTax: summary?.estimatedTax,
    prepaidPoolBalance: summary?.prepaidPoolBalance,
    releasableAmount: summary?.releasable,
  }
}

function mapPrepaidOverviewItem(
  item: RegionTaxPoolOverviewApiItem,
): RegionTaxPoolOverviewItem {
  return {
    ...mapPrepaidOverviewSummary(item),
    amOpenId: item.amOpenId,
    bdOpenId: item.bdOpenId,
    canRelease: item.canRelease,
    comparisonStatus: item.compareStatus,
    comparisonStatusName: item.compareStatusName,
    currency: item.currency,
    customerId: item.customerId,
    customerName: item.customerName,
    rowKey: `${item.customerId}-${item.currency}`,
  }
}

function mapRegionTaxPoolFlowItem(
  item: RegionTaxPoolFlowApiItem,
): RegionTaxPoolFlowItem {
  return {
    accountId: item.accountId,
    changeAmount: item.changeAmount,
    changeCurrency: item.changeCurrency,
    customerId: item.customerId,
    customerName: item.customerName,
    direction: item.direction,
    directionName: item.directionName,
    exchangeRate: item.fxRate,
    flowType: item.flowType,
    flowTypeName: item.flowTypeName,
    id: item.id,
    occurredAt: item.occurredAt,
    poolChangeAmount: item.poolChangeAmount,
    poolCurrency: item.poolCurrency,
    refNo: item.refNo,
    remark: item.remark,
  }
}

function mapTaxRangeConfigItem(
  item: RegionTaxRangeConfigApiItem,
): RegionTaxRangeConfigItem {
  return {
    id: item.id,
    media: item.mediaPlatform,
    accountType: item.accountType,
    effectiveStartDate: item.effectiveFrom,
    effectiveEndDate: getEffectiveEndDate(item.effectiveTo),
    status: item.status,
    updater: item.updatedBy,
    updateTime: item.updatedAt,
  }
}

function mapCountryTaxRateItem(
  item: RegionCountryTaxRateApiItem,
): RegionCountryTaxRateItem {
  return {
    id: item.id,
    media: item.mediaPlatform,
    countryCode: item.countryCode,
    countryName: item.countryName,
    taxRate: `${item.taxRate}%`,
    effectiveStartDate: item.effectiveFrom,
    effectiveEndDate: getEffectiveEndDate(item.effectiveTo),
    status: item.status,
    updater: item.updatedBy,
    updateTime: item.updatedAt,
  }
}

function mapCustomerPrepayRatioItem(
  item: RegionTaxPrepayCustomerApiItem,
): RegionTaxPrepayCustomerItem {
  return {
    accountType: item.accountType,
    accountTypeName: item.accountTypeName,
    customerId: item.customerId,
    customerName: item.customerName,
    id: item.id,
    isExplicitZero: item.prepaidRate === 0,
    media: item.mediaPlatform,
    ratio: item.prepaidRate,
    status: item.status,
    statusName: item.statusName,
    updatedAt: item.updatedAt,
    updatedBy: item.updatedBy,
  }
}

function mapAdAccountPrepayRatioItem(
  item: RegionTaxPrepayAccountApiItem,
): RegionTaxPrepayAccountItem {
  return {
    accountType: item.accountType,
    accountTypeName: item.accountTypeName,
    adAccountId: item.accountId,
    accountName: item.accountName,
    canDelete: item.canDelete,
    configSource: item.configSource,
    configSourceName: item.configSourceName,
    customerId: item.customerId,
    customerName: item.customerName,
    id: item.id,
    isExplicitZero: item.prepaidRate === 0,
    media: item.mediaPlatform,
    ratio: item.prepaidRate,
    status: item.status,
    statusName: item.statusName,
    updatedAt: item.updatedAt,
    updatedBy: item.updatedBy,
  }
}

export const regionTaxFeeApi = defineApi('operation', ({ prefix }) => {
  function getRegionTaxFeeListParams(
    params: PagingRequest<RegionTaxFeeListParams>,
  ) {
    return {
      ...params.condition,
      customerId: params.condition.customerId
        ? Number(params.condition.customerId)
        : undefined,
    }
  }

  function getRegionTaxFeeList(params: PagingRequest<RegionTaxFeeListParams>) {
    return requestClient
      .post(prefix('locationFee/query'), {
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
        orderBy: params.orderBy,
        condition: getRegionTaxFeeQueryCondition(params.condition),
      })
      .then((res) => ({
        items: res.list.map((item: RegionTaxFeeApiItem) =>
          mapRegionTaxFeeItem(item),
        ),
        summary: res.summary,
        total: res.total,
      }))
  }

  function getRegionTaxFeeQueryCondition(params: RegionTaxFeeListParams) {
    const condition = getRegionTaxFeeListParams({
      condition: params,
      pageIndex: 1,
      pageSize: 1,
    })

    return {
      spendDate: DateRanges.dates(condition.dateRange),
      merchantId: condition.customerId,
      customerName: condition.customerName,
      accountId: condition.advertiserAccountId,
      accountName: condition.accountName,
      mediaPlatform: condition.media,
      accountType: condition.accountType,
      countryCode: condition.country,
    }
  }

  function getPrepaidPoolOverviewCondition(
    params: RegionTaxPoolOverviewListParams,
  ): RegionTaxPoolOverviewApiCondition {
    const condition = {
      ...params,
      customerId: params.customerId ? Number(params.customerId) : undefined,
    }

    return {
      amOpenIds: condition.amOpenIds,
      bdOpenIds: condition.bdOpenIds,
      compareStatus: condition.comparisonStatus,
      currency: condition.currency,
      customerId: condition.customerId,
      customerName: condition.customerName,
    }
  }

  function getRegionTaxPoolFlowCondition(
    params: RegionTaxPoolFlowListParams,
  ): RegionTaxPoolFlowApiCondition {
    const condition = {
      ...params,
      customerId: params.customerId ? Number(params.customerId) : undefined,
    }

    return {
      timeRange: formatDateRange(condition.occurredRange),
      customerId: condition.customerId,
      customerName: condition.customerName,
      direction: condition.direction,
      flowType: condition.flowType,
      refNo: condition.refNo,
    }
  }

  /**
   * 查询预收池流水列表。
   * POST /locationFee/flow
   */
  function getRegionTaxPoolFlowList(params: RegionTaxPoolFlowPagingParams) {
    return requestClient
      .post<RegionTaxPoolFlowApiResponse>(prefix('locationFee/flow'), {
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy ?? 'occurredAt',
        orderBy: params.orderBy ?? 'DESC',
        condition: getRegionTaxPoolFlowCondition(params.condition),
      })
      .then((res) => ({
        items: (res.list ?? []).map((item) => mapRegionTaxPoolFlowItem(item)),
        summary: res.summary,
        total: res.total ?? 0,
      }))
  }

  /**
   * 预收总览查询。
   * POST /locationFee/overview
   */
  function getPrepaidPoolOverviewList(
    params: RegionTaxPoolOverviewPagingParams,
  ) {
    return requestClient
      .post<RegionTaxPoolOverviewApiResponse>(prefix('locationFee/overview'), {
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
        orderBy: params.orderBy,
        condition: getPrepaidPoolOverviewCondition(params.condition),
      })
      .then((res) => ({
        estimatedTaxUpdatedAt: res.estimatedTaxUpdatedAt,
        items: (res.list ?? []).map((item) => mapPrepaidOverviewItem(item)),
        summary: mapPrepaidOverviewSummary(res.summary),
        total: res.total ?? 0,
      }))
  }

  /**
   * 释放预收池到钱包。
   * POST /locationFee/release
   */
  function releasePrepaidPoolToWallet(
    params: ReleaseRegionTaxPrepaidPoolParams,
  ) {
    return requestClient.post(prefix('locationFee/release'), {
      customerId: params.customerId,
      releaseAmount: Number(params.releaseAmount),
    })
  }

  function getTaxRangeConfigList(params = DEFAULT_CONFIG_LIST_PARAMS) {
    return requestClient
      .post(prefix('locationFee/scope/query'), {
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
        orderBy: params.orderBy,
        condition: {
          mediaPlatform: params.condition.media,
          accountType: params.condition.accountType,
          status:
            params.condition.status === undefined
              ? null
              : Number(params.condition.status),
        },
      })
      .then((res) => ({
        items: res.list.map((item: RegionTaxRangeConfigApiItem) =>
          mapTaxRangeConfigItem(item),
        ),
        total: res.total,
      }))
  }

  function saveTaxRangeConfig(params: SaveRegionTaxRangeConfigParams) {
    return requestClient.post(prefix('locationFee/scope/save'), {
      id: params.id ?? 0,
      mediaPlatform: params.media,
      accountType: params.accountType,
      effectiveFrom: formatDate(params.effectiveStartDate),
      endMode: getEndMode(params.effectiveEndDate),
      effectiveTo: formatDate(params.effectiveEndDate) ?? null,
      status: params.status,
    })
  }

  function disableTaxRangeConfig(id: number) {
    return requestClient.post(prefix('locationFee/scope/updateStatus'), {
      id,
      status: 2,
    })
  }

  function enableTaxRangeConfig(id: number) {
    return requestClient.post(prefix('locationFee/scope/updateStatus'), {
      id,
      status: 1,
    })
  }

  function getCountryTaxRateList(params = DEFAULT_CONFIG_LIST_PARAMS) {
    return requestClient
      .post(prefix('locationFee/countryRate/query'), {
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
        sortBy: params.sortBy,
        orderBy: params.orderBy,
        condition: {
          mediaPlatform: params.condition.media,
          countryCode: params.condition.country,
          status:
            params.condition.status === undefined
              ? null
              : Number(params.condition.status),
        },
      })
      .then((res) => ({
        items: res.list.map((item: RegionCountryTaxRateApiItem) =>
          mapCountryTaxRateItem(item),
        ),
        total: res.total,
      }))
  }

  function saveCountryTaxRate(params: SaveRegionCountryTaxRateParams) {
    return requestClient.post(prefix('locationFee/countryRate/save'), {
      id: params.id ?? 0,
      mediaPlatform: params.media,
      countryCode: params.countryCode.trim().toUpperCase(),
      countryName: params.countryName?.trim(),
      taxRate: Number(params.taxRate),
      effectiveFrom: formatDate(params.effectiveStartDate),
      endMode: getEndMode(params.effectiveEndDate),
      effectiveTo: formatDate(params.effectiveEndDate) ?? null,
      status: params.status,
    })
  }

  function disableCountryTaxRate(id: number) {
    return requestClient.post(prefix('locationFee/countryRate/updateStatus'), {
      id,
      status: 2,
    })
  }

  function enableCountryTaxRate(id: number) {
    return requestClient.post(prefix('locationFee/countryRate/updateStatus'), {
      id,
      status: 1,
    })
  }

  function getCustomerPrepayRatioCondition(
    params: RegionTaxPrepayCustomerListParams,
  ) {
    const customerId = String(params.customerId ?? '').trim()

    return {
      accountType: params.accountType,
      customerId: customerId ? Number(customerId) : undefined,
      customerName: params.customerName?.trim(),
      mediaPlatform: params.media,
      status: Number(params.status),
    }
  }

  function getAdAccountPrepayRatioCondition(
    params: RegionTaxPrepayAccountListParams,
  ) {
    return {
      accountIds:
        params.accountIds && params.accountIds.length > 0
          ? params.accountIds
          : undefined,
      accountName: params.accountName,
      configSource: params.configSource,
      customerIds:
        params.customerIds && params.customerIds.length > 0
          ? params.customerIds
          : undefined,
      customerName: params.customerName,
      mediaPlatform: params.media,
      status: params.status === undefined ? undefined : Number(params.status),
    }
  }

  /**
   * 客户矩阵 K% 查询。
   * POST /locationFee/rate/customer/query
   */
  function getCustomerPrepayRatioList(
    params: PagingRequest<RegionTaxPrepayCustomerListParams>,
  ) {
    return requestClient
      .post<RegionTaxPrepayCustomerApiResponse>(
        prefix('locationFee/rate/customer/query'),
        {
          condition: getCustomerPrepayRatioCondition(params.condition),
          orderBy: params.orderBy,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
        },
      )
      .then((res) => ({
        items: (res.list ?? []).map((item) => mapCustomerPrepayRatioItem(item)),
        total: res.total ?? 0,
      }))
  }

  /**
   * 客户矩阵 K% 新增/编辑。
   * POST /locationFee/rate/customer/save
   */
  function saveCustomerPrepayRatio(params: SaveRegionTaxPrepayCustomerParams) {
    return requestClient.post(prefix('locationFee/rate/customer/save'), {
      accountType: params.accountType,
      customerId: Number(params.customerId),
      id: params.id,
      mediaPlatform: params.media,
      prepaidRate: Number(params.ratio),
      status: params.status === undefined ? undefined : Number(params.status),
    })
  }

  /**
   * 账户覆盖 K% 查询。
   * POST /locationFee/rate/account/query
   */
  function getAdAccountPrepayRatioList(
    params: PagingRequest<RegionTaxPrepayAccountListParams>,
  ) {
    return requestClient
      .post<RegionTaxPrepayAccountApiResponse>(
        prefix('locationFee/rate/account/query'),
        {
          condition: getAdAccountPrepayRatioCondition(params.condition),
          orderBy: params.orderBy,
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
        },
      )
      .then((res) => ({
        items: (res.list ?? []).map((item) =>
          mapAdAccountPrepayRatioItem(item),
        ),
        unmatchedAccountIds: res.unmatchedAccountIds ?? [],
        unmatchedCustomerIds: res.unmatchedCustomerIds ?? [],
        total: res.total ?? 0,
      }))
  }

  /**
   * 批量新增/编辑账户覆盖 K%。
   * POST /locationFee/rate/account/save
   */
  function saveAdAccountPrepayRatio(params: SaveRegionTaxPrepayAccountParams) {
    return requestClient.post<RegionTaxPrepayAccountSaveResult>(
      prefix('locationFee/rate/account/save'),
      {
        accountIds: parseAdAccountIds(params.accountIds),
        dryRun: params.dryRun,
        mediaPlatform: params.mediaPlatform,
        prepaidRate: Number(params.ratio),
        status: params.status === undefined ? undefined : Number(params.status),
      },
    )
  }

  /**
   * 批量删除账户覆盖 K%。
   * POST /locationFee/rate/account/delete
   */
  function deleteAdAccountPrepayRatio(
    params: DeleteRegionTaxPrepayAccountParams,
  ) {
    return requestClient.post(prefix('locationFee/rate/account/delete'), params)
  }

  return {
    getPrepaidPoolOverviewCondition,
    getPrepaidPoolOverviewList,
    releasePrepaidPoolToWallet,
    getRegionTaxPoolFlowCondition,
    getRegionTaxPoolFlowList,
    getRegionTaxFeeList,
    getRegionTaxFeeListParams,
    getRegionTaxFeeQueryCondition,
    getTaxRangeConfigList,
    saveTaxRangeConfig,
    disableTaxRangeConfig,
    enableTaxRangeConfig,
    getCountryTaxRateList,
    saveCountryTaxRate,
    disableCountryTaxRate,
    enableCountryTaxRate,
    getCustomerPrepayRatioList,
    saveCustomerPrepayRatio,
    getAdAccountPrepayRatioList,
    saveAdAccountPrepayRatio,
    deleteAdAccountPrepayRatio,
  }
})
